import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Page from "components/Page/Page";
import Card from "components/Card/Card";
import Button from "components/Button/Regular";
import { useActiveOrg } from "context/ActiveOrgContext";
import vgToast from "utils/toast";
import {
  GravePlot,
  PLOT_STATUS,
  PLOT_STATUS_LABEL,
  PLOT_TYPE,
  PLOT_TYPE_LABEL,
  PlotStatus,
  PlotType,
  addGravePlots,
  canMove,
  changePlotStatus,
  deleteGravePlot,
  plotPlace,
  readGravePlots,
} from "services/grave-plots.service";

const inputClass = "rounded-md border border-slate-300 px-3 py-2 text-sm";

const statusClass: Record<PlotStatus, string> = {
  1: "bg-emerald-50 text-emerald-700",
  2: "bg-amber-50 text-amber-700",
  3: "bg-slate-100 text-slate-600",
};

/**
 * A cemetery's plots: what is available, reserved or sold, and adding new rows. A plot
 * only moves forward (available, reserved, sold) or is released from a reservation.
 */
const GravePlots = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { orgId } = useActiveOrg();
  const [plots, setPlots] = React.useState<GravePlot[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [filter, setFilter] = React.useState<PlotStatus | 0>(0);

  const [section, setSection] = React.useState("");
  const [plotRow, setPlotRow] = React.useState("");
  const [fromNumber, setFromNumber] = React.useState(1);
  const [toNumber, setToNumber] = React.useState(10);
  const [plotType, setPlotType] = React.useState<PlotType>(PLOT_TYPE.SINGLE);
  const [adding, setAdding] = React.useState(false);

  const load = React.useCallback(async () => {
    if (orgId <= 0) {
      setPlots([]);
      return;
    }
    setLoading(true);
    setError(null);
    const token = await getAccessTokenSilently();
    const { data, error: readError } = await readGravePlots(token, orgId);
    setLoading(false);
    if (readError) setError(readError.message);
    else setPlots(data ?? []);
  }, [getAccessTokenSilently, orgId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const counts = React.useMemo(
    () => ({
      1: plots.filter((p) => p.status === PLOT_STATUS.AVAILABLE).length,
      2: plots.filter((p) => p.status === PLOT_STATUS.RESERVED).length,
      3: plots.filter((p) => p.status === PLOT_STATUS.SOLD).length,
    }),
    [plots]
  );

  const shown = filter ? plots.filter((p) => p.status === filter) : plots;

  const move = async (plot: GravePlot, status: PlotStatus) => {
    let holder = plot.holderName;
    if (status !== PLOT_STATUS.AVAILABLE) {
      const answer = window.prompt("Who is it held for?", plot.holderName);
      if (answer === null) return;
      holder = answer;
    } else if (!window.confirm(`Release ${plotPlace(plot)}? It becomes available again.`)) {
      return;
    }
    const token = await getAccessTokenSilently();
    const { error: moveError } = await changePlotStatus(token, plot, status, holder);
    if (moveError) {
      vgToast.error(moveError.message);
      return;
    }
    vgToast.success(`${plotPlace(plot)} is now ${PLOT_STATUS_LABEL[status].toLowerCase()}.`);
    await load();
  };

  const remove = async (plot: GravePlot) => {
    if (!window.confirm(`Remove ${plotPlace(plot)}? Only do this for a plot added by mistake.`)) return;
    const token = await getAccessTokenSilently();
    const { error: deleteError } = await deleteGravePlot(token, plot);
    if (deleteError) {
      vgToast.error(deleteError.message);
      return;
    }
    await load();
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    try {
      const token = await getAccessTokenSilently();
      const { data, error: addError } = await addGravePlots(token, {
        orgId,
        section,
        plotRow,
        fromNumber,
        toNumber,
        plotType,
      });
      if (addError) {
        vgToast.error(addError.message);
        return;
      }
      const total = toNumber - fromNumber + 1;
      vgToast.success(
        data === total ? `${data} plots added.` : `${data} of ${total} plots added; the rest were already there.`
      );
      await load();
    } finally {
      setAdding(false);
    }
  };

  if (orgId <= 0) {
    return (
      <Page title="Grave plots">
        <p className="text-sm text-slate-500">Choose an active provider from the header.</p>
      </Page>
    );
  }

  return (
    <Page title="Grave plots" subTitle="Your cemetery's plots, and which are available, reserved or sold.">
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-3 gap-4">
          {([1, 2, 3] as PlotStatus[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(filter === s ? 0 : s)}
              className={`rounded-[10px] border bg-white p-4 text-left ${
                filter === s ? "border-slate-500" : "border-slate-200"
              }`}
            >
              <span className="block text-2xl font-semibold text-slate-900">{counts[s]}</span>
              <span className="block text-sm text-slate-500">{PLOT_STATUS_LABEL[s]}</span>
            </button>
          ))}
        </div>

        <Card title={filter ? `${PLOT_STATUS_LABEL[filter]} plots` : "All plots"}>
          <div className="p-6">
            {loading && <p className="text-sm text-slate-500">Loading plots…</p>}
            {error && <p className="text-sm text-red-500">{error}</p>}
            {!loading && !error && shown.length === 0 && (
              <p className="text-sm text-slate-500">
                {plots.length === 0 ? "No plots yet. Add a row of plots below." : "No plots with this status."}
              </p>
            )}
            {shown.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="py-2 font-medium">Plot</th>
                    <th className="py-2 font-medium">Type</th>
                    <th className="py-2 font-medium">Status</th>
                    <th className="py-2 font-medium">Held for</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {shown.map((p) => (
                    <tr key={p.plotId}>
                      <td className="py-2 text-slate-800">{plotPlace(p)}</td>
                      <td className="py-2 text-slate-600">{PLOT_TYPE_LABEL[p.plotType]}</td>
                      <td className="py-2">
                        <span className={`rounded px-2 py-0.5 text-xs font-medium ${statusClass[p.status]}`}>
                          {PLOT_STATUS_LABEL[p.status]}
                        </span>
                      </td>
                      <td className="py-2 text-slate-600">{p.holderName}</td>
                      <td className="py-2 text-right whitespace-nowrap">
                        {canMove(p.status, PLOT_STATUS.RESERVED) && (
                          <button type="button" className="ml-3 text-slate-700 hover:underline" onClick={() => move(p, PLOT_STATUS.RESERVED)}>
                            Reserve
                          </button>
                        )}
                        {canMove(p.status, PLOT_STATUS.SOLD) && (
                          <button type="button" className="ml-3 text-slate-700 hover:underline" onClick={() => move(p, PLOT_STATUS.SOLD)}>
                            Sell
                          </button>
                        )}
                        {canMove(p.status, PLOT_STATUS.AVAILABLE) && (
                          <button type="button" className="ml-3 text-slate-700 hover:underline" onClick={() => move(p, PLOT_STATUS.AVAILABLE)}>
                            Release
                          </button>
                        )}
                        {p.status === PLOT_STATUS.AVAILABLE && (
                          <button type="button" className="ml-3 text-red-600 hover:underline" onClick={() => remove(p)}>
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>

        <Card title="Add a row of plots" subTitle="Numbers from the first to the last; plots already there are skipped.">
          <form onSubmit={add} className="p-6 grid grid-cols-2 gap-4 sm:grid-cols-5">
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Section
              <input className={inputClass} value={section} onChange={(e) => setSection(e.target.value)} placeholder="e.g. B" />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Row
              <input className={inputClass} value={plotRow} onChange={(e) => setPlotRow(e.target.value)} placeholder="optional" />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              From number
              <input className={inputClass} type="number" min={1} value={fromNumber} onChange={(e) => setFromNumber(Number(e.target.value))} />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              To number
              <input className={inputClass} type="number" min={1} value={toNumber} onChange={(e) => setToNumber(Number(e.target.value))} />
            </label>
            <label className="flex flex-col gap-1.5 text-sm text-slate-600">
              Type
              <select className={inputClass} value={plotType} onChange={(e) => setPlotType(Number(e.target.value) as PlotType)}>
                {(Object.keys(PLOT_TYPE_LABEL) as unknown as PlotType[]).map((t) => (
                  <option key={t} value={t}>
                    {PLOT_TYPE_LABEL[t]}
                  </option>
                ))}
              </select>
            </label>
            <div className="col-span-2 sm:col-span-5">
              <Button color="Primary" type="submit" disabled={adding}>
                <span>{adding ? "Adding…" : "Add plots"}</span>
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Page>
  );
};

export default GravePlots;
